import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import type { SourcingEventV2Dto } from "../../../../autogen/bff-api";
import { useCurrentMoment } from "../../../sourcing-events/portal/utils/useCurrentMoment";
import { deriveTimelinePhase, phasesFor, type TimelinePhaseKey } from "./deriveTimelinePhase";

// Compact labels for the details-page progress stepper. English keys; Norwegian values already
// exist in locales (Kunngjøring / Tilbudsfrist / Evaluering / Tildeling / Karens / Kontrakt).
// Listed so langen keeps them — keys are referenced dynamically below:
// t('Announcement') t('Bid deadline') t('Evaluation') t('Award') t('Standstill') t('Contract')
// t('Request deadline') t('Qualification & selection')
const PHASE_LABEL_KEYS: Record<TimelinePhaseKey, string> = {
  announcement: "Announcement",
  requestDeadline: "Request deadline",
  qualification: "Qualification & selection",
  questionDeadline: "Question deadline",
  bidDeadline: "Bid deadline",
  evaluationStart: "Evaluation",
  awardDecision: "Award",
  standstillExpiry: "Standstill",
  contractSigning: "Contract",
};

const dotShadow = (isActive: boolean, isDone: boolean): string =>
  isActive
    ? "0 0 0 4px var(--chakra-colors-smBackground), 0 0 0 6px var(--chakra-colors-smPrimary)"
    : isDone
      ? "0 0 0 1.5px var(--chakra-colors-smPrimary)"
      : "0 0 0 1.5px var(--chakra-colors-smBorder)";

interface Props {
  event: SourcingEventV2Dto;
}

export const EventPhaseTimeline = ({ event }: Props) => {
  const { t } = useTranslation();

  // Ticks ~every minute so the stepper crosses the bid-deadline / standstill boundary on its own
  // while the page stays open, without a manual refresh.
  const now = useCurrentMoment().valueOf();
  const phase = deriveTimelinePhase(event, now);
  if (!phase.visible) return null;

  const phases = phasesFor(event);
  const completed = new Set(phase.completedKeys);

  return (
    <Box
      mt="6"
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p={{ base: "4", md: "6" }}
      opacity={phase.idle ? 0.5 : 1}
    >
      <Box position="relative">
        {/* Connecting rail */}
        <Box
          position="absolute"
          bg="smBorder"
          zIndex={0}
          top="13px"
          left={{ base: "13px", md: "3%" }}
          right={{ base: "auto", md: "3%" }}
          bottom={{ base: "13px", md: "auto" }}
          height={{ base: "auto", md: "2px" }}
          width={{ base: "2px", md: "auto" }}
        />

        <Flex
          direction={{ base: "column", md: "row" }}
          position="relative"
          zIndex={1}
          gap={{ base: 4, md: 2 }}
        >
          {phases.map((key, index) => {
            const isActive = phase.activeKey === key;
            const isDone = completed.has(key);
            const filled = isActive || isDone;

            return (
              <Flex
                key={key}
                flex={{ md: "1 1 0" }}
                direction={{ base: "row", md: "column" }}
                align={{ base: "flex-start", md: "center" }}
                gap={{ base: 3, md: 2 }}
                px={{ md: 2 }}
                aria-current={isActive ? "step" : undefined}
              >
                <Flex
                  align="center"
                  justify="center"
                  boxSize="7"
                  flexShrink={0}
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="bold"
                  bg={filled ? "smPrimary" : "smBackground"}
                  color={filled ? "white" : "smMuted"}
                  boxShadow={dotShadow(isActive, isDone)}
                >
                  {isDone ? <Icon as={FiCheck} boxSize="3.5" /> : index + 1}
                </Flex>
                <Text
                  fontSize="sm"
                  fontWeight={isActive ? "semibold" : "medium"}
                  color={isActive ? "smText" : "smMuted"}
                  textAlign={{ base: "left", md: "center" }}
                  lineHeight="1.2"
                >
                  {t(PHASE_LABEL_KEYS[key])}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
};
